<template>
  <div class="d-flex" style="height: 100%;">
    <!-- Thread sidebar -->
    <div class="thread-sidebar">
      <v-overlay absolute :value="isThreadsLoading">
        <v-progress-circular indeterminate size="64" color="primary" />
      </v-overlay>
      <div class="d-flex flex-column pa-2" style="gap: 8px">
        <v-text-field v-model="threadSearchInput" label="Search chats..." dense outlined hide-details
          prepend-inner-icon="mdi-magnify" clearable />
        <v-btn block text class="new-chat-btn" @click="startNewChat">
          <v-icon>mdi-plus</v-icon>
          <span>New Chat</span>
        </v-btn>
      </div>
      <v-divider />
      <v-virtual-scroll :bench="10" :items="filteredThreads" height="800" item-height="72"
        style="flex: 1; overflow-y: auto;">
        <template v-slot:default="{ item }">
          <v-list-item :key="item.id" @click="selectThread(item)" :input-value="activeThreadId === item.id"
            :class="['thread-list-item', { 'thread-list-item--busy': isThreadBusy(item.id) }]">
            <v-list-item-content>
              <div v-if="item.version === 'v1'" class="deprecated-text">Deprecated</div>
              <div v-if="item.updatedAt" class="updated-date">{{ formatTimestamp(item.updatedAt) }}</div>
              <div class="menu-item-text">{{ item.title || 'Untitled' }}</div>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon x-small @click.stop="deleteThread(item)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </template>
      </v-virtual-scroll>
    </div>

    <!-- Chat area -->
    <div class="chat-area d-flex flex-column py-4">
      <v-overlay absolute :value="isChatLoading">
        <v-progress-circular indeterminate size="64" color="primary" />
      </v-overlay>

      <div v-if="showCreditsChip" class="d-flex justify-end align-center px-3 mb-2" style="gap: 6px;">
        <div
          v-for="chip in creditsChips.filter((c) => c.loaded)"
          :key="chip.key"
          class="tracey-credits-chip"
          :class="{
            [`tracey-credits-chip--${chip.key}`]: true,
            'tracey-credits-chip--empty': chip.empty,
            'tracey-credits-chip--low': chip.low && !chip.empty,
          }"
          :title="chip.tooltip"
        >
          <v-icon x-small class="tracey-credits-chip__icon">mdi-lightning-bolt</v-icon>
          <span class="tracey-credits-chip__agent">{{ chip.key === 'ultra' ? 'Ultra' : 'Sprint' }}</span>
          <span>{{ chip.label }}</span>
        </div>
        <v-btn
          small
          depressed
          class="tracey-topup-btn"
          aria-label="Top up Tracey credits"
          title="Top up Tracey credits"
          @click="showTopUpModal = true"
        >
          <v-icon left small color="#fff">mdi-plus-circle-outline</v-icon>
          Top up
        </v-btn>
        <v-btn
          icon
          small
          aria-label="View Tracey usage"
          title="View Tracey usage"
          @click="$router.push('/usage')"
        >
          <v-icon small color="#667085">mdi-chart-bar</v-icon>
        </v-btn>
      </div>

      <tracey-credits-topup-modal v-model="showTopUpModal" :default-agent="traceyVersion" />

      <div ref="chatContainer" class="mb-4 px-3" style="flex: 1; overflow-y: auto;">
        <!-- Deprecated v1 thread (read-only) -->
        <template v-if="activeThread && activeThread.version === 'v1'">
          <div v-for="(message, idx) in activeThreadMessages" :key="`v1-${idx}`" class="mb-6">
            <div class="chat-bubble user-message">
              <div class="created-date">{{ formatTimestamp(message.createdAt) }}</div>
              <div class="message-text">{{ message.userInput }}</div>
            </div>
            <div v-if="message.response" class="chat-bubble system-message">
              <div class="created-date">{{ formatTimestamp(message.createdAt) }}</div>
              <div class="message-text" v-html="parseMarkdown(message.response)" />
              <span v-if="isQueryDataPresent(message)">
                <v-btn class="mt-2 mb-2 mr-3" small color="primary" @click="viewQueryData(message)">
                  View Data
                </v-btn>
              </span>
            </div>
          </div>
          <div v-if="activeThread" class="created-date">{{ activeThread.id }}</div>
        </template>

        <!-- v2 thread: messages + streaming (thread-scoped) -->
        <template v-else>
          <chat-message-bubble v-for="(msg, idx) in displayMessages" :key="`msg-${msg.id || idx}`" :message="msg"
            :is-query-data-present="isQueryDataPresent" :format-timestamp="formatTimestamp"
            @view-data="viewQueryData(msg)" />
          <!-- Streaming response: only show when streaming for THIS thread -->
          <streaming-message v-if="isStreamingInActiveThread" :content="activeThreadStreamingState.content"
            :pending-tools="activeThreadStreamingState.pendingTools" />
          <div v-else-if="submittingThreadIds[activeThreadId] && !streamingByThreadId[activeThreadId]" class="status">
            <v-progress-circular indeterminate color="primary" size="16" width="2" />
            <span>Thinking...</span>
          </div>
        </template>
      </div>

      <!-- Input area -->
      <div class="px-3" style="position: relative; flex-shrink: 0; z-index: 1;">
        <div v-if="activeThread && activeThread.version === 'v1'"
          class="d-flex justify-center align-center text-center deprecated-overlay"
          style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0, 0, 0, 0.8); color: white; z-index: 20;">
          Deprecated... Create a new thread to continue with the new Tracey.
        </div>
        <div class="chat-input-wrapper">
          <textarea v-model="inputText" placeholder="Describe the type of notices you are looking for"
            @keypress.enter.prevent="submitMessage" @input="autoGrowTextarea" ref="textareaInput"
            class="custom-textarea"
            style="width: 100%; min-height: 100px; padding: 8px; border: none; outline: none; resize: none; font-size: 0.9rem; line-height: 1.5; background: transparent;" />
          <div v-if="outOfCredits" class="credits-notice">
            <v-icon x-small class="credits-notice__icon">mdi-information-outline</v-icon>
            <span>
              You're out of {{ traceyVersion === 'ultra' ? 'Ultra' : 'Sprint' }} credits<template v-if="creditsResetLabel"> until {{ creditsResetLabel }}</template>. You can
              <button type="button" class="credits-notice__link" @click="showTopUpModal = true">top up now</button>.
            </span>
          </div>
          <div class="d-flex justify-space-between align-center px-2 pb-2">
            <div class="d-flex align-center">
              <tracey-agent-select v-model="traceyVersion" />
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn text small @click="togglePromptLibrary" :color="showPromptLibrary ? 'primary' : 'grey darken-2'"
                    class="prompt-library-btn" v-bind="attrs" v-on="on">
                    <v-icon left small
                      :color="showPromptLibrary ? 'primary' : 'grey darken-2'">mdi-book-open-variant</v-icon>
                    Prompt Library
                  </v-btn>
                </template>
                <span>Browse and use saved prompts</span>
              </v-tooltip>
            </div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn v-if="isActiveThreadBusy" icon @click="stopGeneration" v-bind="attrs" v-on="on"
                  class="stop-gen-btn">
                  <v-icon color="#ff1463">mdi-stop-circle</v-icon>
                </v-btn>
                <v-btn v-else icon color="primary" @click="submitMessage" :disabled="!inputText.trim() || outOfCredits" v-bind="attrs"
                  v-on="on"
                  style="pointer-events: auto; visibility: visible !important; display: block !important;">
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </template>
              <span>{{ isActiveThreadBusy ? 'Stop generating' : 'Send message' }}</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </div>

    <!-- Prompt Library Sidebar -->
    <transition name="slide-fade">
      <div v-if="showPromptLibrary" class="prompt-library-sidebar">
        <div class="prompt-library-header">
          <h3 class="title font-weight-bold" style="font-size: 18px;">Prompt Library</h3>
          <v-btn icon small @click="togglePromptLibrary">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="pa-3">
          <v-text-field v-model="promptSearchInput" label="Search prompts..." dense outlined hide-details
            prepend-inner-icon="mdi-magnify" clearable class="mb-3" />
          <div class="d-flex flex-wrap" style="gap: 8px;">
            <v-chip v-for="category in promptTypes" :key="category" small
              :color="selectedPromptType === category ? 'primary' : 'default'"
              :outlined="selectedPromptType !== category" @click="selectedPromptType = category"
              style="cursor: pointer;">
              {{ category }}
            </v-chip>
          </div>
        </div>
        <v-divider />
        <div class="flex-grow-1" style="overflow-y: auto; padding: 8px; position: relative;">
          <div v-if="isPromptsLoading" class="d-flex justify-center align-center" style="height: 200px;">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <template v-else>
            <div v-for="prompt in filteredPrompts" :key="prompt.id" class="prompt-item pa-3 mb-2"
              @click="usePrompt(prompt)">
              <div class="d-flex align-center mb-2">
                <v-chip x-small :color="getPromptTypeColor(prompt.type)" class="mr-2">{{ prompt.type }}</v-chip>
                <v-spacer />
                <v-icon x-small color="grey">mdi-chevron-right</v-icon>
              </div>
              <h4 class="font-weight-bold mb-1" style="font-size: 14px;">{{ prompt.title }}</h4>
              <p class="mb-0" style="font-size: 12px; color: #666; line-height: 1.4;">{{ prompt.description }}</p>
            </div>
            <div v-if="!isPromptsLoading && filteredPrompts.length === 0" class="prompt-empty-state text-center pa-4">
              <v-icon large color="grey lighten-1">mdi-book-search-outline</v-icon>
              <p class="mt-2 mb-0">No prompts found</p>
            </div>
          </template>
        </div>
      </div>
    </transition>

    <!-- Tool call selection modal -->
    <v-dialog v-model="showToolCallModal" max-width="600" persistent>
      <v-card>
        <v-card-title class="headline">Select Query Data to View</v-card-title>
        <v-card-text>
          <p class="mb-4">Multiple queries were found. Please select which one you'd like to view:</p>
          <v-list>
            <v-list-item v-for="(toolCall, index) in matchingToolCalls" :key="index"
              @click="selectToolCallAndView(toolCall)" class="mb-2"
              style="border: 1px solid #e0e0e0; border-radius: 4px; cursor: pointer;">
              <v-list-item-content>
                <v-list-item-title>{{ getToolCallDisplayName(toolCall.name) }}</v-list-item-title>
                <v-list-item-subtitle v-if="getToolCallDisplaySummary(toolCall)" class="tool-call-summary">
                  {{ getToolCallDisplaySummary(toolCall) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showToolCallModal = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { marked } from 'marked';
import {
  submitTraceyQuery,
  fetchUserThreadsAPI,
  fetchMessagesForThreadAPI,
  deleteTraceyThread,
  stopTraceyThread,
  fetchPromptLibrary,
} from './APIManager.js';
import ChatMessageBubble from './tracey/ChatMessageBubble.vue';
import StreamingMessage from './tracey/StreamingMessage.vue';
import TraceyAgentSelect from './tracey/page/TraceyAgentSelect.vue';
import TraceyCreditsTopupModal from './tracey/TraceyCreditsTopupModal.vue';
import creditsMixin from './tracey/creditsMixin.js';

const QUERY_DATA_TOOLS = ['get_leaderboard', 'search_tenders', 'get_market_insights', 'get_search_summary'];

const TOOL_TO_VIEW_INDEX = {
  get_leaderboard: 1,
  search_tenders: 0,
  get_market_insights: 3,
  get_search_summary: 0,
};

export default {
  name: 'TraceyChatWindow',
  mixins: [creditsMixin],
  components: { ChatMessageBubble, StreamingMessage, TraceyAgentSelect, TraceyCreditsTopupModal },
  props: {
    isFullScreen: Boolean,
  },
  data() {
    return {
      inputText: '',
      // "sprint" | "ultra" -- which Tracey agent the NEXT query goes to. Both
      // agents share the same thread_id server-side, so switching mid-thread
      // still sees the full prior transcript regardless of which agent wrote it.
      traceyVersion: 'ultra',
      showTopUpModal: false,
      threads: [],
      activeThread: null,
      activeThreadId: null,
      /** @type {Record<string, Array>} Messages keyed by thread ID */
      messagesByThreadId: {},
      threadSearchInput: '',
      isChatLoading: false,
      isThreadsLoading: false,
      /** Per-thread: thread IDs that sent a request but stream not started yet */
      submittingThreadIds: {},
      /** Per-thread: { content, pendingTools, allToolCalls } for active streams */
      streamingByThreadId: {},
      /** Per-thread: AbortController for each in-flight request */
      streamAbortControllers: {},
      showToolCallModal: false,
      toolCallModalMessage: null,
      matchingToolCalls: [],
      showPromptLibrary: false,
      promptSearchInput: '',
      selectedPromptType: 'All',
      promptTypes: ['All'],
      prompts: [],
      isPromptsLoading: false,
    };
  },
  computed: {
    filteredThreads() {
      if (!this.threadSearchInput?.trim()) return this.threads;
      const term = this.threadSearchInput.toLowerCase().trim();
      return this.threads.filter(
        (t) => (t.title && t.title.toLowerCase().includes(term)) || (t.id && String(t.id).toLowerCase().includes(term))
      );
    },
    activeThreadMessages() {
      if (!this.activeThreadId) return [];
      return this.messagesByThreadId[this.activeThreadId] ?? [];
    },
    /** v2 messages only; excludes v1 format */
    displayMessages() {
      const msgs = this.activeThreadMessages;
      return msgs.filter((m) => m.version !== 'v1' && (m.role === 'user' || m.role === 'assistant'));
    },
    /** Streaming state for the currently visible thread (for template) */
    activeThreadStreamingState() {
      if (!this.activeThreadId) return null;
      return this.streamingByThreadId[this.activeThreadId] || null;
    },
    isStreamingInActiveThread() {
      return !!this.activeThreadStreamingState;
    },
    /** True if the active thread has a request in flight or is streaming */
    isActiveThreadBusy() {
      if (!this.activeThreadId) return false;
      return !!(this.submittingThreadIds[this.activeThreadId] || this.streamingByThreadId[this.activeThreadId]);
    },
    filteredPrompts() {
      let filtered = this.prompts;
      if (this.selectedPromptType && this.selectedPromptType !== 'All') {
        filtered = filtered.filter((p) => p.type === this.selectedPromptType);
      }
      if (this.promptSearchInput?.trim()) {
        const term = this.promptSearchInput.toLowerCase().trim();
        filtered = filtered.filter(
          (p) =>
            (p.title && p.title.toLowerCase().includes(term)) ||
            (p.description && p.description.toLowerCase().includes(term)) ||
            (p.prompt && p.prompt.toLowerCase().includes(term))
        );
      }
      return filtered;
    },
  },
  watch: {
    activeThreadMessages: { handler: 'scrollToBottom', deep: true },
    activeThreadStreamingState: { handler: 'scrollToBottom', deep: true },
    showPromptLibrary(val) {
      if (val && !this.isFullScreen) this.$emit('toggle-fullscreen');
      if (val && this.prompts.length === 0 && !this.isPromptsLoading) this.fetchPrompts();
    },
    traceyVersion(version) {
      // Sprint and Ultra each have their own wallet, so switching agents
      // means showing a different balance, not just relabelling the chip.
      this.loadCredits(version);
    },
  },
  mounted() {
    this.fetchThreads();
    this.fetchPrompts();
    this.loadAllCredits();
  },
  beforeDestroy() {
    // Do not abort in-flight streams; they continue in the background.
    // User must explicitly stop a thread via the Stop button.
  },
  methods: {
    parseMarkdown(text) {
      return text ? marked.parse(text) : '';
    },
    formatTimestamp(ts) {
      if (!ts) return 'Invalid Date';
      const normalized = /([Z]|[+-]\d{2}:\d{2})$/.test(ts) ? ts : ts + 'Z';
      const date = new Date(normalized);
      if (isNaN(date)) return 'Invalid Date';
      return date.toLocaleString(undefined, {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    autoGrowTextarea() {
      this.$nextTick(() => {
        const el = this.$refs.textareaInput;
        if (el) {
          el.style.height = 'auto';
          el.style.height = el.scrollHeight + 'px';
        }
      });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatContainer;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    async fetchThreads() {
      this.isThreadsLoading = true;
      try {
        const response = await fetchUserThreadsAPI();
        this.threads = (response || []).map((t) => ({
          id: t.thread_id,
          version: t.version,
          title: t.title,
          createdAt: t.created_at,
          updatedAt: t.updated_at,
        })).sort((a, b) => {
          if (!a.updatedAt && !b.updatedAt) return 0;
          if (!a.updatedAt) return 1;
          if (!b.updatedAt) return -1;
          return new Date(b.updatedAt) - new Date(a.updatedAt);
        });
      } finally {
        this.isThreadsLoading = false;
      }
    },

    async fetchPrompts() {
      this.isPromptsLoading = true;
      try {
        const response = await fetchPromptLibrary();
        this.prompts = (response || []).map((p) => ({
          id: p.id,
          title: p.title,
          description: p.prompt,
          prompt: p.prompt,
          type: p.type,
          createdAt: p.created_at,
          updatedAt: p.updated_at,
        }));
        const types = new Set(['All']);
        this.prompts.forEach((p) => p.type && types.add(p.type));
        this.promptTypes = Array.from(types).sort();
      } catch (e) {
        console.error('Error fetching prompts:', e);
        this.prompts = [];
      } finally {
        this.isPromptsLoading = false;
      }
    },

    async selectThread(thread) {
      this.isChatLoading = true;
      try {
        const response = await fetchMessagesForThreadAPI(thread.id);
        const msgs = (response || []).map((msg) =>
          msg.version === 'v1'
            ? {
              id: msg.id,
              version: 'v1',
              userInput: msg.user_input,
              response: msg.response,
              querystring: msg.querystring,
              createdAt: msg.created_at,
            }
            : {
              id: msg.id,
              version: 'v2',
              threadId: msg.thread_id,
              role: msg.role,
              content: msg.content,
              toolCalls: msg.tool_calls,
              fileAttachment: msg.file_attachment
                ? {
                  filename: msg.file_attachment.filename,
                  downloadUrl: msg.file_attachment.download_url,
                }
                : null,
              createdAt: msg.created_at,
            }
        );
        this.$set(this.messagesByThreadId, thread.id, msgs);
        this.activeThread = thread;
        this.activeThreadId = thread.id;
      } catch (e) {
        this.$set(this.messagesByThreadId, thread.id, []);
      } finally {
        this.isChatLoading = false;
      }
    },

    startNewChat() {
      this.inputText = '';
      this.activeThread = null;
      this.activeThreadId = null;
    },

    async deleteThread(thread) {
      this.isThreadsLoading = true;
      try {
        await deleteTraceyThread(thread.id);
        this.$delete(this.messagesByThreadId, thread.id);
        if (thread.id === this.activeThreadId) this.startNewChat();
        await this.fetchThreads();
      } finally {
        this.isThreadsLoading = false;
      }
    },

    addMessageToThread(threadId, message) {
      const list = this.messagesByThreadId[threadId] ?? [];
      if (!this.messagesByThreadId[threadId]) {
        this.$set(this.messagesByThreadId, threadId, list);
      }
      list.push(message);
    },

    ensureThreadInList(threadId, title = 'Untitled') {
      const exists = this.threads.some((t) => t.id === threadId);
      if (!exists) {
        this.threads.unshift({
          id: threadId,
          version: 'v2',
          title,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        this.threads.sort((a, b) => {
          const au = a.updatedAt ? new Date(a.updatedAt) : 0;
          const bu = b.updatedAt ? new Date(b.updatedAt) : 0;
          return bu - au;
        });
      }
    },

    abortStream() {
      Object.keys(this.streamAbortControllers).forEach((key) => {
        if (key !== 'pending') {
          stopTraceyThread(key).catch(() => {});
        }
        this.streamAbortControllers[key].abort();
      });
      this.streamAbortControllers = {};
    },

    stopGeneration() {
      const threadId = this.activeThreadId;
      if (!threadId) return;

      const controller = this.streamAbortControllers[threadId];
      if (controller) {
        controller.abort();
        this.$delete(this.streamAbortControllers, threadId);
      }

      if (threadId !== 'pending') {
        stopTraceyThread(threadId).catch((err) => {
          console.error('Stop API error:', err);
        });
      }

      const streamState = this.streamingByThreadId[threadId];
      if (streamState && streamState.content) {
        this.addMessageToThread(threadId, {
          id: crypto.randomUUID?.() || `msg-${Date.now()}`,
          version: 'v2',
          role: 'assistant',
          content: streamState.content,
          toolCalls: streamState.allToolCalls || [],
          createdAt: new Date().toISOString(),
        });
      }

      this.$delete(this.streamingByThreadId, threadId);
      this.$delete(this.submittingThreadIds, threadId);
    },

    async submitMessage() {
      if (this.isActiveThreadBusy) return;
      if (this.outOfCredits) return;
      const text = this.inputText?.trim();
      if (!text) return;

      const timestamp = new Date().toISOString();
      const targetThreadId = this.activeThreadId;
      const myKey = targetThreadId || 'pending';

      const userMessage = {
        id: `temp-${Date.now()}`,
        version: 'v2',
        role: 'user',
        content: text,
        toolCalls: [],
        createdAt: timestamp,
      };

      if (targetThreadId) {
        this.addMessageToThread(targetThreadId, userMessage);
      } else {
        this.$set(this.messagesByThreadId, 'pending', [userMessage]);
        this.activeThreadId = 'pending';
        this.activeThread = { id: 'pending', version: 'v2', title: 'New Chat' };
      }

      this.inputText = '';
      this.$set(this.submittingThreadIds, myKey, true);
      const controller = new AbortController();
      this.$set(this.streamAbortControllers, myKey, controller);

      let currentKey = myKey;
      try {
        const response = await submitTraceyQuery(text, targetThreadId || '', {
          signal: controller.signal,
          version: this.traceyVersion,
        });
        if (!response.ok) {
          const errBody = await response.text();
          const creditKind = this.handleCreditHttpError(response.status, errBody);
          if (creditKind) {
            // Credit block / in-flight lock: nothing was generated, so roll
            // back the optimistic user bubble and restore the input.
            if (targetThreadId) {
              const list = this.messagesByThreadId[targetThreadId];
              if (list && list[list.length - 1] === userMessage) list.pop();
            } else {
              this.$delete(this.messagesByThreadId, 'pending');
              this.activeThreadId = null;
              this.activeThread = null;
            }
            this.inputText = text;
            if (creditKind === 'query_already_running') {
              window.alert('A Tracey query is already running. Please wait for it to finish or stop it.');
            }
            return;
          }
          const err = new Error(`Query failed: ${response.status} ${response.statusText}`);
          err.status = response.status;
          err.body = errBody;
          throw err;
        }
        if (!response.body) throw new Error('No response body');

        this.$delete(this.submittingThreadIds, myKey);
        this.$set(this.streamingByThreadId, myKey, {
          content: '',
          pendingTools: [],
          allToolCalls: [],
          fileAttachment: null,
        });

        if (!targetThreadId) {
          this.activeThreadId = 'pending';
          this.activeThread = { id: 'pending', version: 'v2', title: 'New Chat' };
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamThreadId = targetThreadId;

        while (true) {
          const { done, value } = await reader.read();
          if (this._isDestroyed) break;
          if (value) buffer += decoder.decode(value, { stream: true });

          let newlineIdx;
          while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
            const line = buffer.slice(0, newlineIdx);
            buffer = buffer.slice(newlineIdx + 1);
            if (!line.trim()) continue;
            try {
              const chunk = JSON.parse(line);
              if (chunk.type === 'balance') {
                this.applyCreditSnapshot(chunk);
                continue;
              }
              if (chunk.thread_id && chunk.thread_id !== streamThreadId) {
                streamThreadId = chunk.thread_id;
                if (myKey === 'pending') {
                  const state = this.streamingByThreadId['pending'];
                  this.$delete(this.streamingByThreadId, 'pending');
                  this.$set(this.streamingByThreadId, streamThreadId, state);
                  this.$set(this.streamAbortControllers, streamThreadId, this.streamAbortControllers['pending']);
                  this.$delete(this.streamAbortControllers, 'pending');
                  currentKey = streamThreadId;
                }
                this.ensureThreadInList(streamThreadId);
                if (!targetThreadId) {
                  const pending = this.messagesByThreadId['pending'] ?? [];
                  this.$delete(this.messagesByThreadId, 'pending');
                  this.$set(this.messagesByThreadId, streamThreadId, pending);
                  this.activeThreadId = streamThreadId;
                  this.activeThread = this.threads.find((t) => t.id === streamThreadId) || {
                    id: streamThreadId,
                    version: 'v2',
                    title: 'Untitled',
                  };
                }
              }
              const stateKey = currentKey;
              const state = this.streamingByThreadId[stateKey];
              if (state) {
                if (chunk.type === 'token') {
                  state.content += chunk.message || '';
                  state.pendingTools = [];
                } else if (chunk.type === 'tool_calls') {
                  state.pendingTools = chunk.message || [];
                  state.allToolCalls = [...(state.allToolCalls || []), ...(chunk.message || [])];
                } else if (chunk.type === 'file') {
                  state.fileAttachment = {
                    filename: chunk.filename,
                    downloadUrl: chunk.download_url,
                  };
                }
              }
            } catch (e) {
              console.error('Parse chunk error:', line, e);
            }
          }

          if (done) {
            if (this._isDestroyed) break;
            const remaining = decoder.decode();
            if (remaining) buffer += remaining;
            if (buffer.trim()) {
              try {
                const chunk = JSON.parse(buffer);
                if (chunk.type === 'balance') {
                  this.applyCreditSnapshot(chunk);
                } else {
                if (chunk.thread_id && chunk.thread_id !== streamThreadId) {
                  streamThreadId = chunk.thread_id;
                  if (myKey === 'pending') {
                    const state = this.streamingByThreadId['pending'];
                    this.$delete(this.streamingByThreadId, 'pending');
                    this.$set(this.streamingByThreadId, streamThreadId, state);
                    this.$set(this.streamAbortControllers, streamThreadId, this.streamAbortControllers['pending']);
                    this.$delete(this.streamAbortControllers, 'pending');
                    currentKey = streamThreadId;
                  }
                  this.ensureThreadInList(streamThreadId);
                  if (!targetThreadId) {
                    const pending = this.messagesByThreadId['pending'] ?? [];
                    this.$delete(this.messagesByThreadId, 'pending');
                    this.$set(this.messagesByThreadId, streamThreadId, pending);
                    this.activeThreadId = streamThreadId;
                    this.activeThread = this.threads.find((t) => t.id === streamThreadId) || {
                      id: streamThreadId,
                      version: 'v2',
                      title: 'Untitled',
                    };
                  }
                }
                const stateKey = currentKey;
                const state = this.streamingByThreadId[stateKey];
                if (state) {
                  if (chunk.type === 'token') {
                    state.content += chunk.message || '';
                    state.pendingTools = [];
                  } else if (chunk.type === 'tool_calls') {
                    state.pendingTools = chunk.message || [];
                    state.allToolCalls = [...(state.allToolCalls || []), ...(chunk.message || [])];
                  } else if (chunk.type === 'file') {
                    state.fileAttachment = {
                      filename: chunk.filename,
                      downloadUrl: chunk.download_url,
                    };
                  }
                }
                }
              } catch (e) {
                console.error('Parse final chunk error:', buffer, e);
              }
            }

            const finalThreadId = streamThreadId || currentKey;
            const finalState = this.streamingByThreadId[finalThreadId];
            if (finalThreadId && finalState) {
              const assistantMessage = {
                id: crypto.randomUUID?.() || `msg-${Date.now()}`,
                version: 'v2',
                role: 'assistant',
                content: finalState.content,
                toolCalls: finalState.allToolCalls || [],
                fileAttachment: finalState.fileAttachment || null,
                createdAt: timestamp,
              };
              this.addMessageToThread(finalThreadId, assistantMessage);
            }
            this.$delete(this.streamingByThreadId, finalThreadId);
            this.$delete(this.streamAbortControllers, finalThreadId);
            break;
          }
        }
        if (this._isDestroyed) return;
        await this.fetchThreads();
      } catch (error) {
        if (this._isDestroyed) return;
        if (error.name === 'AbortError') return;
        const errMsg = "Sorry, I couldn't understand your request. Try rewording or expanding your query.";
        const fallbackId = this.activeThreadId || myKey;
        const assistantMessage = {
          id: crypto.randomUUID?.() || `msg-${Date.now()}`,
          version: 'v2',
          role: 'assistant',
          content: errMsg,
          toolCalls: [],
          createdAt: timestamp,
        };
        this.addMessageToThread(fallbackId, assistantMessage);
        if (fallbackId === 'pending') {
          this.$set(this.messagesByThreadId, 'pending', this.messagesByThreadId['pending'] || []);
        }
        console.error('Tracey query error:', error?.status ? `${error.status}: ${error.body || error.message}` : error);
      } finally {
        if (this._isDestroyed) return;
        this.$delete(this.submittingThreadIds, myKey);
        this.$delete(this.streamingByThreadId, currentKey);
        this.$delete(this.streamAbortControllers, currentKey);
      }
    },

    isThreadBusy(threadId) {
      return !!(this.submittingThreadIds[threadId] || this.streamingByThreadId[threadId]);
    },
    isQueryDataPresent(message) {
      if (message?.toolCalls?.length) {
        return message.toolCalls.some((c) => QUERY_DATA_TOOLS.includes(c.name));
      }
      return !!message?.querystring;
    },

    getMatchingToolCalls(message) {
      if (!message?.toolCalls?.length) return [];
      return message.toolCalls.filter((c) => QUERY_DATA_TOOLS.includes(c.name));
    },

    viewQueryData(message) {
      if (message?.version === 'v1') {
        const query = this.queryStringToJson(message.querystring || '');
        query.viewIndex = 0;
        this.$router.push({ path: '/search', query });
        return;
      }
      if (message?.version === 'v2') {
        const matching = this.getMatchingToolCalls(message);
        if (matching.length >= 1) {
          this.matchingToolCalls = matching;
          this.toolCallModalMessage = message;
          this.showToolCallModal = true;
          return;
        }
        const toolCall = matching[0];
        if (toolCall) {
          const viewIndex = TOOL_TO_VIEW_INDEX[toolCall.name] ?? 0;
          const query = { viewIndex };
          for (const key in toolCall.args || {}) {
            if (key === 'auth_token' || key === 'size') continue;
            if (key === 'leaderboard_type') query.leaderboardIndex = this.inferLeaderboardIndex(toolCall.args[key]);
            else query[key] = toolCall.args[key];
          }
          this.$router.push({ path: '/search', query });
        }
      }
    },

    selectToolCallAndView(toolCall) {
      this.showToolCallModal = false;
      const msg = this.toolCallModalMessage;
      if (msg && toolCall) {
        const viewIndex = TOOL_TO_VIEW_INDEX[toolCall.name] ?? 0;
        const query = { viewIndex };
        for (const key in toolCall.args || {}) {
          if (key === 'auth_token' || key === 'size') continue;
          if (key === 'leaderboard_type') query.leaderboardIndex = this.inferLeaderboardIndex(toolCall.args[key]);
          else query[key] = toolCall.args[key];
        }
        this.$router.push({ path: '/search', query });
      }
      this.toolCallModalMessage = null;
      this.matchingToolCalls = [];
    },

    getToolCallDisplayName(name) {
      const map = {
        get_leaderboard: 'Leaderboard',
        search_tenders: 'Search Tenders',
        get_market_insights: 'Market Insights',
        get_search_summary: 'Search Summary',
      };
      return map[name] || name;
    },

    getToolCallDisplaySummary(toolCall) {
      const args = toolCall?.args;
      if (!args) return '';
      const skip = ['auth_token', 'size'];
      const parts = [];
      if (args.query) parts.push(`Query: ${args.query}`);
      if (args.leaderboard_type) parts.push(`Type: ${args.leaderboard_type}`);
      for (const key of Object.keys(args)) {
        if (skip.includes(key) || key === 'query' || key === 'leaderboard_type') continue;
        const v = args[key];
        if (v != null && v !== '') parts.push(`${key}: ${v}`);
      }
      return parts.join(' · ');
    },

    queryStringToJson(str) {
      if (str.startsWith('?')) str = str.slice(1);
      const params = new URLSearchParams(str);
      const result = {};
      params.forEach((v, k) => {
        if (result[k]) result[k] = Array.isArray(result[k]) ? [...result[k], v] : [result[k], v];
        else result[k] = v;
      });
      return result;
    },

    inferLeaderboardIndex(type) {
      const map = { publisher: 0, category: 1, supplier: 2 };
      return map[type];
    },

    togglePromptLibrary() {
      this.showPromptLibrary = !this.showPromptLibrary;
    },

    usePrompt(prompt) {
      this.inputText = prompt.prompt || '';
      this.$nextTick(() => {
        const el = this.$el?.querySelector('textarea');
        if (el) el.focus();
      });
    },

    getPromptTypeColor(type) {
      const map = { Search: 'primary', Analysis: 'secondary', Insights: 'success', Reports: 'warning' };
      return map[type] || 'grey';
    },
  },
};
</script>

<style scoped>
/* Tracey credit balance chip */
.tracey-topup-btn {
  background: #ff1463 !important;
  color: #fff !important;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  box-shadow: none !important;
}

.tracey-topup-btn .v-icon {
  color: #fff !important;
}

.tracey-credits-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 10px;
  border-radius: 13px;
  background: #f2f4f7;
  border: 1px solid #e4e7ec;
  color: #475467;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: default;
}

.tracey-credits-chip__icon {
  color: #667085 !important;
}

.tracey-credits-chip__agent {
  font-weight: 500;
  color: #98a2b3;
}

.tracey-credits-chip--ultra .tracey-credits-chip__icon {
  color: #ff1463 !important;
}

.tracey-credits-chip--sprint .tracey-credits-chip__icon {
  color: #0891b2 !important;
}

.tracey-credits-chip--low {
  background: #fff8eb;
  border-color: #fedf89;
  color: #b54708;
}

.tracey-credits-chip--low .tracey-credits-chip__icon,
.tracey-credits-chip--low .tracey-credits-chip__agent {
  color: #dc6803 !important;
}

.tracey-credits-chip--empty {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}

.tracey-credits-chip--empty .tracey-credits-chip__icon,
.tracey-credits-chip--empty .tracey-credits-chip__agent {
  color: #d92d20 !important;
}

/* Brand colors */
.thread-sidebar {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 250px;
  min-width: 250px;
  height: 100%;
  border-right: 1px solid rgba(18, 37, 58, 0.12);
  background-color: #fafbfc;
}

.thread-list-item {
  position: relative;
  min-height: 72px;
  max-height: 72px;
  overflow: hidden;
}

.thread-list-item .v-list-item__content {
  overflow: hidden;
  min-width: 0;
}

.thread-list-item:hover {
  background-color: #dddddd;
}

::v-deep .thread-list-item.v-list-item--active {
  color: unset !important;
  background-color: #dddddd !important;
}

.thread-list-item--busy {
  animation: thread-pulse 1.5s ease-in-out infinite;
}

@keyframes thread-pulse {
  0%, 100% {
    background-color: #dddddd;
  }
  50% {
    background-color: rgba(255, 20, 99, 0.12);
  }
}
 
.new-chat-btn {
  color: #12253a !important;
  font-weight: 500;
}

.new-chat-btn:hover {
  background-color: #e6f0f5 !important;
  color: #ff1463 !important;
}

.chat-input-wrapper {
  border: 1px solid rgba(18, 37, 58, 0.25);
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.chat-input-wrapper:focus-within {
  border-color: #ff1463;
  box-shadow: 0 0 0 1px #ff1463;
}

.prompt-library-btn {
  background-color: #e6f0f5 !important;
  color: #12253a !important;
}

.prompt-library-sidebar {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 350px;
  min-width: 350px;
  height: 100%;
  overflow-y: auto;
  border-left: 1px solid rgba(18, 37, 58, 0.12);
  background-color: #fafbfc;
}

.prompt-library-header {
  border-bottom: 1px solid rgba(18, 37, 58, 0.12);
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 10;
  padding: 12px;
}

.prompt-library-header .title {
  color: #12253a;
  font-size: 18px;
}

.prompt-item {
  border: 1px solid rgba(18, 37, 58, 0.12);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.prompt-item:hover {
  background-color: #e6f0f5 !important;
  border-color: rgba(255, 20, 99, 0.3);
}

.prompt-empty-state {
  color: #6b7280;
}

.chat-area {
  position: relative;
  flex: 1;
  height: 100%;
  max-width: 1000px;
  margin-inline: auto;
  min-width: 0;
  background-color: #fff;
}

.created-date,
.updated-date {
  font-size: 10px;
  color: #6b7280;
}

.deprecated-text {
  font-size: 12px;
  color: #ff1463;
}

.menu-item-text {
  font-size: 14px;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.prompt-library-sidebar {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-in;
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.prompt-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.custom-textarea {
  font-family: inherit;
}

.custom-textarea:focus {
  outline: none !important;
  border: none !important;
}

.custom-textarea::placeholder {
  color: rgba(0, 0, 0, 0.38);
}

.credits-notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0 8px 6px;
  padding: 7px 10px;
  border-radius: 8px;
  background: #f2f4f7;
  color: #667085;
  font-size: 12px;
  line-height: 1.5;
}

.credits-notice__icon {
  color: #98a2b3 !important;
  margin-top: 1px;
}

.credits-notice__link {
  font: inherit;
  color: #ff1463;
  font-weight: 600;
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.tool-call-summary {
  white-space: normal !important;
  line-height: 1.4;
}

.stop-gen-btn {
  pointer-events: auto;
  animation: pulse-stop 1.5s ease-in-out infinite;
}

@keyframes pulse-stop {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>
